// frontend\src\pages\Profile\NotificationSettings.js
import { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import { BiBell, BiFilm, BiStats, BiSend } from "react-icons/bi";

const NotificationSettings = ({ authTokens }) => {
  const [settings, setSettings] = useState({
    // Основные настройки
    enable_all_notifications: true,
    
    // Уведомления о пленках
    notify_film_spent: true,
    notify_film_low_stock: true,
    notify_film_expiring: true,
    notify_film_expired: true,
    
    // Ежедневная статистика
    enable_daily_stats: false,
    daily_stats_time: "19:00",
    
    // Содержимое статистики
    include_film_stats: true,
    include_chemical_stats: true,
    include_expiring_items: true,
    include_low_stock: true,
    include_investment: true,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Выносим fetchSettings в useCallback
  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get("/notification-settings/", {
        headers: {
          Authorization: `Bearer ${authTokens?.access}`,
        },
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Ошибка загрузки настроек:", error);
      toast.error("Ошибка загрузки настроек уведомлений");
    }
  }, [authTokens]); 

  // Загрузка настроек
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]); 

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await api.put("/notification-settings/", settings, {
        headers: {
          Authorization: `Bearer ${authTokens?.access}`,
        },
      });
      toast.success("Настройки уведомлений сохранены");
    } catch (error) {
      console.error("Ошибка сохранения настроек:", error);
      toast.error("Ошибка сохранения настроек");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async (type = "general") => {
    setIsTesting(true);
    try {
      await api.post(
        "/telegram/test-notification/",
        { type },
        {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
          },
        }
      );
      toast.success("Тестовое уведомление отправлено");
    } catch (error) {
      console.error("Ошибка отправки теста:", error);
      toast.error("Ошибка отправки тестового уведомления");
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="mb-0">
          <BiBell className="me-2" />
          Настройки уведомлений
        </h4>
      </div>
      <div className="card-body">
        {/* Основной переключатель */}
        <div className="mb-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="enableAllNotifications"
              checked={settings.enable_all_notifications}
              onChange={(e) => handleSettingChange("enable_all_notifications", e.target.checked)}
            />
            <label className="form-check-label fw-bold" htmlFor="enableAllNotifications">
              Все уведомления
            </label>
          </div>
          <small className="text-muted">
            Включить/выключить все уведомления сразу
          </small>
        </div>

        {/* Уведомления о пленках */}
        <div className="mb-4">
          <h5 className="d-flex align-items-center mb-3">
            <BiFilm className="me-2" />
            Уведомления о пленках
          </h5>
          
          <div className="row">
            <div className="col-md-6">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="notifyFilmSpent"
                  checked={settings.notify_film_spent}
                  onChange={(e) => handleSettingChange("notify_film_spent", e.target.checked)}
                  disabled={!settings.enable_all_notifications}
                />
                <label className="form-check-label" htmlFor="notifyFilmSpent">
                  Списание пленок
                </label>
              </div>
              
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="notifyFilmLowStock"
                  checked={settings.notify_film_low_stock}
                  onChange={(e) => handleSettingChange("notify_film_low_stock", e.target.checked)}
                  disabled={!settings.enable_all_notifications}
                />
                <label className="form-check-label" htmlFor="notifyFilmLowStock">
                  Мало пленок
                </label>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="notifyFilmExpiring"
                  checked={settings.notify_film_expiring}
                  onChange={(e) => handleSettingChange("notify_film_expiring", e.target.checked)}
                  disabled={!settings.enable_all_notifications}
                />
                <label className="form-check-label" htmlFor="notifyFilmExpiring">
                  Скоро истекает срок
                </label>
              </div>
              
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="notifyFilmExpired"
                  checked={settings.notify_film_expired}
                  onChange={(e) => handleSettingChange("notify_film_expired", e.target.checked)}
                  disabled={!settings.enable_all_notifications}
                />
                <label className="form-check-label" htmlFor="notifyFilmExpired">
                  Просроченные пленки
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Ежедневная статистика */}
        <div className="mb-4">
          <h5 className="d-flex align-items-center mb-3">
            <BiStats className="me-2" />
            Ежедневная статистика
          </h5>
          
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="enableDailyStats"
                  checked={settings.enable_daily_stats}
                  onChange={(e) => handleSettingChange("enable_daily_stats", e.target.checked)}
                  disabled={!settings.enable_all_notifications}
                />
                <label className="form-check-label" htmlFor="enableDailyStats">
                  Включить ежедневную статистику
                </label>
              </div>
            </div>
            
            <div className="col-md-6">
              <label htmlFor="dailyStatsTime" className="form-label">
                Время отправки
              </label>
              <input
                type="time"
                className="form-control"
                id="dailyStatsTime"
                value={settings.daily_stats_time}
                onChange={(e) => handleSettingChange("daily_stats_time", e.target.value)}
                disabled={!settings.enable_daily_stats || !settings.enable_all_notifications}
              />
            </div>
          </div>
          
          {/* Настройки содержимого статистики */}
          {settings.enable_daily_stats && (
            <div className="mt-3 p-3 bg-light rounded">
              <h6>Что включать в статистику:</h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeFilmStats"
                      checked={settings.include_film_stats}
                      onChange={(e) => handleSettingChange("include_film_stats", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="includeFilmStats">
                      Статистика пленок
                    </label>
                  </div>
                  
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeChemicalStats"
                      checked={settings.include_chemical_stats}
                      onChange={(e) => handleSettingChange("include_chemical_stats", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="includeChemicalStats">
                      Статистика химии
                    </label>
                  </div>
                  
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeExpiringItems"
                      checked={settings.include_expiring_items}
                      onChange={(e) => handleSettingChange("include_expiring_items", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="includeExpiringItems">
                      Скоро истекают
                    </label>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeLowStock"
                      checked={settings.include_low_stock}
                      onChange={(e) => handleSettingChange("include_low_stock", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="includeLowStock">
                      Заканчиваются
                    </label>
                  </div>
                  
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeInvestment"
                      checked={settings.include_investment}
                      onChange={(e) => handleSettingChange("include_investment", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="includeInvestment">
                      Инвестиции
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Кнопки действий */}
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Сохранение...
              </>
            ) : (
              "Сохранить настройки"
            )}
          </button>
          
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleTestNotification("general")}
              disabled={isTesting}
            >
              <BiSend className="me-1" />
              {isTesting ? "Отправка..." : "Тест уведомления"}
            </button>
            
            <button
              className="btn btn-outline-info"
              onClick={() => handleTestNotification("film_spent")}
              disabled={isTesting}
            >
              <BiFilm className="me-1" />
              Тест списания
            </button>
            
            <button
              className="btn btn-outline-success"
              onClick={() => handleTestNotification("daily_stats")}
              disabled={isTesting}
            >
              <BiStats className="me-1" />
              Тест статистики
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;